"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { ConsentMode } from "@/lib/consent-jurisdiction";

type ConsentState = "granted" | "denied" | "unknown";

type ConsentContextValue = {
  consentState: ConsentState;
  grantConsent: () => void;
  denyConsent: () => void;
  openPreferences: () => void;
};

const STORAGE_KEY = "sendbot.analyticsConsent";
const CONSENT_COOKIE_NAME = "sendbot_analytics_consent";
const ConsentContext = createContext<ConsentContextValue | null>(null);

const copy = {
  pt: {
    title: "Privacidade e medição",
    body: "Usamos cookies opcionais para medir campanhas, melhorar o Sendbot e entender quais anúncios geram cadastros e assinaturas. Os cookies necessários continuam ativos.",
    essential: "Somente necessários",
    accept: "Aceitar medição",
    close: "Fechar",
  },
  en: {
    title: "Privacy and measurement",
    body: "We use optional cookies to measure campaigns, improve Sendbot and understand which ads lead to signups. Essential cookies remain enabled.",
    essential: "Essential only",
    accept: "Accept measurement",
    close: "Close",
  },
  es: {
    title: "Privacidad y medición",
    body: "Usamos cookies opcionales para medir campañas, mejorar Sendbot y comprender qué anuncios generan registros. Las cookies necesarias siguen activas.",
    essential: "Solo necesarias",
    accept: "Aceptar medición",
    close: "Cerrar",
  },
  fr: {
    title: "Confidentialité et mesure",
    body: "Nous utilisons des cookies facultatifs pour mesurer nos campagnes et améliorer Sendbot. Les cookies essentiels restent actifs.",
    essential: "Uniquement nécessaires",
    accept: "Accepter la mesure",
    close: "Fermer",
  },
  it: {
    title: "Privacy e misurazione",
    body: "Utilizziamo cookie facoltativi per misurare le campagne e migliorare Sendbot. I cookie essenziali restano attivi.",
    essential: "Solo necessari",
    accept: "Accetta la misurazione",
    close: "Chiudi",
  },
  de: {
    title: "Datenschutz und Messung",
    body: "Wir verwenden optionale Cookies zur Kampagnenmessung und zur Verbesserung von Sendbot. Notwendige Cookies bleiben aktiv.",
    essential: "Nur notwendige",
    accept: "Messung akzeptieren",
    close: "Schließen",
  },
  ro: {
    title: "Confidențialitate și măsurare",
    body: "Folosim cookie-uri opționale pentru măsurarea campaniilor și îmbunătățirea Sendbot. Cookie-urile necesare rămân active.",
    essential: "Doar necesare",
    accept: "Acceptă măsurarea",
    close: "Închide",
  },
};

const readConsentCookie = (): Exclude<ConsentState, "unknown"> | undefined => {
  for (const item of document.cookie.split(";")) {
    const separator = item.indexOf("=");
    if (separator < 0) continue;
    if (item.slice(0, separator).trim() !== CONSENT_COOKIE_NAME) continue;
    try {
      const value = decodeURIComponent(item.slice(separator + 1).trim());
      if (value === "granted" || value === "denied") return value;
    } catch {
      // A malformed cookie must not block new consent.
    }
  }
  return undefined;
};

const readPersistedConsent = ():
  | Exclude<ConsentState, "unknown">
  | undefined => {
  try {
    const cookie = readConsentCookie();
    if (cookie) return cookie;
  } catch {
    // Browser may disable cookie reads.
  }
  try {
    const state = window.localStorage.getItem(STORAGE_KEY);
    if (state === "granted" || state === "denied") return state;
  } catch {
    // Browser may disable local storage.
  }
  return undefined;
};

const persistConsent = (state: Exclude<ConsentState, "unknown">) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, state);
  } catch {
    // Cookie below is an independent fallback.
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const hostname = window.location.hostname;
  const domain =
    hostname === "sendbot.co" || hostname.endsWith(".sendbot.co")
      ? "; Domain=.sendbot.co"
      : "";
  try {
    // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not universally available.
    document.cookie =
      CONSENT_COOKIE_NAME +
      "=" +
      state +
      "; Path=/; Max-Age=" +
      180 * 24 * 60 * 60 +
      "; SameSite=Lax" +
      secure +
      domain;
  } catch {
    // In-memory decision remains valid for this session.
  }
};

const updateGoogleConsent = (state: Exclude<ConsentState, "unknown">) => {
  window.dataLayer = window.dataLayer ?? [];
  window.gtag =
    window.gtag ??
    function gtag(...args: Parameters<NonNullable<Window["gtag"]>>) {
      window.dataLayer?.push(args);
    };
  window.gtag("consent", "update", {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
};

const globalPrivacyControlEnabled = () =>
  (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;

async function loadConsentMode(): Promise<ConsentMode> {
  try {
    const response = await fetch("/landing-api/consent-policy", {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
      signal: AbortSignal.timeout(3500),
    });
    if (!response.ok) return "restricted";
    const data: unknown = await response.json();
    if (
      data !== null &&
      typeof data === "object" &&
      "mode" in data &&
      (data.mode === "prompt" ||
        data.mode === "automatic" ||
        data.mode === "restricted")
    )
      return data.mode;
  } catch {
    // No geo or an unavailable endpoint never enables optional tracking.
  }
  return "restricted";
}

export function ConsentProvider({
  children,
  locale = "pt",
}: {
  children: ReactNode;
  locale?: string;
}) {
  const [consentState, setConsentState] = useState<ConsentState>("unknown");
  const [consentMode, setConsentMode] = useState<ConsentMode>("restricted");
  const [consentResolved, setConsentResolved] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const language = locale.split("-")[0] as keyof typeof copy;
  const labels = copy[language] ?? copy.en;

  useEffect(() => {
    let mounted = true;
    const resolveConsent = async () => {
      const explicit = readPersistedConsent();
      const gpc = globalPrivacyControlEnabled();

      // A prior, explicit decision takes precedence across jurisdictions;
      // Global Privacy Control always takes precedence over optional tracking.
      if (explicit || gpc) {
        const choice = gpc ? "denied" : (explicit ?? "denied");
        if (mounted) {
          setConsentState(choice);
          setConsentResolved(true);
        }
        updateGoogleConsent(choice);
        return;
      }

      const mode = await loadConsentMode();
      if (!mounted) return;
      setConsentMode(mode);

      if (mode === "automatic") {
        // Do not persist an automatic choice as if it were explicit consent:
        // moving into an opt-in jurisdiction must prompt next time.
        setConsentState("granted");
        updateGoogleConsent("granted");
      } else if (mode === "restricted") {
        // Brazil and unverified geographies stay frictionless but do not
        // activate tracking/ads without an actual legal basis or user opt-in.
        setConsentState("denied");
        updateGoogleConsent("denied");
      } else {
        setConsentState("unknown");
        updateGoogleConsent("denied");
      }
      setConsentResolved(true);
    };
    void resolveConsent();
    return () => {
      mounted = false;
    };
  }, []);

  const updateConsent = useCallback(
    (state: Exclude<ConsentState, "unknown">) => {
      const effective = globalPrivacyControlEnabled() ? "denied" : state;
      persistConsent(effective);
      setConsentState(effective);
      setPreferencesOpen(false);
      updateGoogleConsent(effective);
      window.dispatchEvent(
        new CustomEvent("sendbot:analytics-consent", { detail: effective }),
      );
    },
    [],
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      consentState,
      grantConsent: () => updateConsent("granted"),
      denyConsent: () => updateConsent("denied"),
      openPreferences: () => setPreferencesOpen(true),
    }),
    [consentState, updateConsent],
  );

  const showPrompt =
    consentResolved &&
    (preferencesOpen ||
      (consentMode === "prompt" && consentState === "unknown"));

  return (
    <ConsentContext.Provider value={value}>
      {children}
      {showPrompt && (
        <div
          data-sendbot-consent-banner
          role="region"
          aria-label={labels.title}
          className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-3xl rounded-2xl border border-white/15 bg-black/95 p-5 text-white shadow-2xl backdrop-blur-xl"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <p className="font-semibold">{labels.title}</p>
              <p className="mt-1 text-sm text-white/70">{labels.body}</p>
            </div>
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className="rounded-xl border border-white/20 px-4 py-2 text-sm font-medium hover:bg-white/10"
                onClick={() => updateConsent("denied")}
              >
                {labels.essential}
              </button>
              <button
                type="button"
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold hover:bg-blue-500"
                onClick={() => updateConsent("granted")}
              >
                {labels.accept}
              </button>
              {preferencesOpen && (
                <button
                  type="button"
                  className="rounded-xl border border-white/20 px-4 py-2 text-sm font-medium hover:bg-white/10"
                  onClick={() => setPreferencesOpen(false)}
                >
                  {labels.close}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </ConsentContext.Provider>
  );
}

export const useAnalyticsConsent = () => {
  const context = useContext(ConsentContext);
  if (!context)
    throw new Error("useAnalyticsConsent must be used inside ConsentProvider");
  return context;
};
